/**
 * DriverHelpPage — `/driver-portal/help`.
 *
 * Pure UI — static FAQ knowledge base + contact strip + open-ticket form.
 * No backend wiring. The ticket form is local-only; once service-service-desk
 * exposes a public `POST /v1/service-desk/tickets` for drivers, swap the
 * stub for a mutation. Safety incidents always go through the SOS button on
 * the mobile-driver app — this page deliberately doesn't render a panic
 * trigger on the web.
 */
import { type FormEvent, type JSX, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  ChevronRight,
  MessageSquare,
  Phone,
  ShieldAlert,
} from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

interface KbArticle {
  readonly id: string;
  readonly cat: string;
  readonly title: string;
  readonly body: string;
}

const ARTICLES: readonly KbArticle[] = [
  {
    id: 'a1',
    cat: 'Getting started',
    title: 'How do I clock in?',
    body: 'Open the Driver app and tap Clock In on the home screen. You will be guided through selfie + liveness, location check, vehicle pairing and the pre-shift inspection.',
  },
  {
    id: 'a2',
    cat: 'Schedule',
    title: 'Why was a stop added to my route?',
    body: 'The route engine reassigns parcels in real time when traffic, customer reschedules or capacity changes. You can accept or reject within 60 seconds.',
  },
  {
    id: 'a3',
    cat: 'Navigation',
    title: 'Turn-by-turn isn’t starting',
    body: 'Make sure Google Maps Navigation SDK has location permission. If the in-app navigator is unavailable, the app falls back to a Google Maps deep-link.',
  },
  {
    id: 'a4',
    cat: 'POD',
    title: 'I can’t capture a signature offline',
    body: 'Signatures are captured locally and sync when you reconnect. Check the connectivity-diagnostics chip; if the queue is large, find a Wi-Fi hotspot at a NIPOST outlet.',
  },
  {
    id: 'a5',
    cat: 'Offline',
    title: 'How long can I stay offline?',
    body: 'Up to 24 hours of GPS pings buffer locally, plus your scans, signatures and photos. Get back online before the buffer fills.',
  },
  {
    id: 'a6',
    cat: 'Account',
    title: 'I lost my phone — what do I do?',
    body: 'Go to /driver-portal/profile → Security → Sign out everywhere on a trusted device. This unbinds the phone and revokes every session.',
  },
  {
    id: 'a7',
    cat: 'Banking-for-payroll',
    title: 'I changed banks — how do I update payroll?',
    body: 'Profile → Banking → Change account. NIBSS Name Enquiry must match your registered name. The change applies after a 24-hour cooling-off; an SMS cancel code is provided.',
  },
  {
    id: 'a8',
    cat: 'Safety',
    title: 'When should I trigger SOS?',
    body: 'Anytime you feel unsafe. Use NEED_HELP_QUIET if you cannot show distress on screen. The app will hot-mic for 60 seconds and notify the safety officer.',
  },
];

const CATEGORIES: readonly string[] = [
  'All',
  'Getting started',
  'Schedule',
  'Navigation',
  'POD',
  'Offline',
  'Account',
  'Banking-for-payroll',
  'Safety',
];

export function DriverHelpPage(): JSX.Element {
  const [search, setSearch] = useState<string>('');
  const [cat, setCat] = useState<string>('All');
  const [showTicketForm, setShowTicketForm] = useState<boolean>(false);
  const [ticketSubmitted, setTicketSubmitted] = useState<boolean>(false);

  const filtered = useMemo(() => {
    return ARTICLES.filter((a) => {
      if (cat !== 'All' && a.cat !== cat) return false;
      if (search.trim().length === 0) return true;
      const needle = search.toLowerCase();
      return (
        a.title.toLowerCase().includes(needle) ||
        a.body.toLowerCase().includes(needle)
      );
    });
  }, [search, cat]);

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setShowTicketForm(false);
    setTicketSubmitted(true);
  }

  return (
    <div>
      <PageHeader
        title="Help & support"
        description="Search the knowledge base or open a ticket. Safety incidents always go through the SOS button on your mobile app."
      />

      {/* Safety nudge */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-red-900">
            In an emergency, use the SOS button on the Driver mobile app.
          </p>
          <p className="text-red-800/90 text-xs">
            Triple-tap or 3-second long-press triggers the panic flow —
            GPS escalation, mic capture, and dispatch / safety officer paged.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* KB search + list */}
        <div className="lg:col-span-2">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search the knowledge base"
          />
          <div className="flex flex-wrap gap-1 mb-3">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md ${
                  cat === c
                    ? 'bg-nipost-green-light text-nipost-green'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {filtered.map((a) => (
              <div
                key={a.id}
                className="bg-white border border-border rounded-xl p-4"
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {a.title}
                  </p>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted bg-gray-100 px-2 py-0.5 rounded">
                    {a.cat}
                  </span>
                </div>
                <p className="text-xs text-muted">{a.body}</p>
              </div>
            ))}
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-muted">
                <BookOpen className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">
                  No articles match. Try opening a support ticket.
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {/* Right column — contact + ticket */}
        <div className="space-y-4">
          <div className="bg-white border border-border rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4 text-nipost-green" /> Talk to someone
            </h3>
            <div className="space-y-2 text-sm">
              <a
                href="tel:0800NIPOST"
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-700"
              >
                <span className="inline-flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  Driver Services 0800-NIPOST
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </a>
              <button
                type="button"
                disabled
                title="Live chat — mobile only"
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-gray-400 cursor-not-allowed"
              >
                <span className="inline-flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-gray-300" />
                  Live chat with dispatch (mobile)
                </span>
                <ChevronRight className="w-4 h-4 text-gray-200" />
              </button>
            </div>
          </div>

          <div className="bg-white border border-border rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-warning" /> Open a support ticket
            </h3>
            {ticketSubmitted ? (
              <p className="text-xs text-success bg-success-light rounded-lg p-3">
                Ticket noted locally. Public ticket submission is being prepared.
              </p>
            ) : !showTicketForm ? (
              <>
                <p className="text-xs text-muted mb-3">
                  We will attach your shift, vehicle, app version and last sync
                  automatically.
                </p>
                <button
                  type="button"
                  onClick={() => setShowTicketForm(true)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-nipost-green text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-nipost-green-dark"
                >
                  Open ticket
                </button>
              </>
            ) : (
              <form className="space-y-3" onSubmit={onSubmit}>
                <div>
                  <label
                    htmlFor="driver-help-subject"
                    className="block text-[11px] font-semibold text-gray-700 mb-1 uppercase tracking-widest"
                  >
                    Subject
                  </label>
                  <input
                    id="driver-help-subject"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    placeholder="Briefly describe the issue"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="driver-help-body"
                    className="block text-[11px] font-semibold text-gray-700 mb-1 uppercase tracking-widest"
                  >
                    Details
                  </label>
                  <textarea
                    id="driver-help-body"
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-none"
                    placeholder="What happened? When? On which stop?"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowTicketForm(false)}
                    className="px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-xs font-medium text-white bg-nipost-green rounded-lg"
                  >
                    Submit
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
